import React, { useEffect, useState } from "react";
import { useAppSettings, resolveLogo } from "../../lib/AppSettingsContext";
import { PhotoUploader } from "../shared/PhotoUploader";
import StructuralBackButton from "../shared/StructuralBackButton";
import { CustomIcon } from "../shared/CustomIcon";
import { CoverImage } from "../shared/CoverImage";
import { TextStyleControls } from "../shared/TextStyleControls";
import { kcFetch, API_BASE, authHeaders } from "../../lib/apiClient";
import { COLOR_PRESETS } from "../services/ServiceForm";
import { DashboardHeroImagesSection } from "./DashboardHeroImagesSection";
import { toast } from "sonner";

const CATEGORY_DEFS = [
  { key: "service",     label: "Services",     fallback: "var(--kc-accent)" },
  { key: "event",       label: "Events",       fallback: "#E8C84A" },
  { key: "appointment", label: "Appointments", fallback: "#5FB1FF" },
  { key: "birthday",    label: "Birthdays",    fallback: "#FF8FB6" },
  { key: "blockout",    label: "Blockouts",    fallback: "#FF8A65" },
];

// ============================================================
// SettingsPage — Super Admin only. Lets the SA control global
// branding (logo, app name, tagline), rename the Follow-Ups
// feature, and set the default follow-up inactivity threshold
// for new team members. Per-user overrides remain editable on
// each user's record.
// ============================================================
export default function SettingsPage({ currentUser, isSuperAdmin, addAudit, addNotification }) {
  const { settings, update, reload } = useAppSettings();
  const [form, setForm] = useState({
    appName: settings.appName,
    appTagline: settings.appTagline,
    appLogo: settings.appLogo,
    followUpsLabel: settings.followUpsLabel,
    followUpsIcon: settings.followUpsIcon || "",
    followUpsCoverImage: settings.followUpsCoverImage || "",
    followUpsTextColor: settings.followUpsTextColor || "white",
    followUpsTextEffect: settings.followUpsTextEffect || "shadow",
    followUpsOverlay: typeof settings.followUpsOverlay === "number" ? settings.followUpsOverlay : 55,
    defaultInactivityDays: settings.defaultInactivityDays,
  });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    setForm({
      appName: settings.appName,
      appTagline: settings.appTagline,
      appLogo: settings.appLogo,
      followUpsLabel: settings.followUpsLabel,
      followUpsIcon: settings.followUpsIcon || "",
      followUpsCoverImage: settings.followUpsCoverImage || "",
      followUpsTextColor: settings.followUpsTextColor || "white",
      followUpsTextEffect: settings.followUpsTextEffect || "shadow",
      followUpsOverlay: typeof settings.followUpsOverlay === "number" ? settings.followUpsOverlay : 55,
      defaultInactivityDays: settings.defaultInactivityDays,
    });
  }, [settings]);

  if (!isSuperAdmin) {
    return (
      <div data-testid="settings-locked" style={{ padding: 40, textAlign: "center", color: "var(--kc-sub)", display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        <CustomIcon name="lock" size={18} /> Only Super Admins can change app settings.
      </div>
    );
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError("");
    const trimmedName = (form.appName || "").trim();
    const trimmedLabel = (form.followUpsLabel || "").trim();
    const n = Number(form.defaultInactivityDays);
    if (!trimmedName) { setError("App name cannot be empty."); return; }
    if (!trimmedLabel) { setError("Follow-Ups label cannot be empty."); return; }
    if (!Number.isFinite(n) || n < 1 || n > 365) { setError("Default inactivity must be between 1 and 365 days."); return; }
    setSaving(true);
    try {
      await update({
        appName: trimmedName,
        appTagline: (form.appTagline || "").trim(),
        appLogo: form.appLogo || "",
        followUpsLabel: trimmedLabel,
        followUpsIcon: form.followUpsIcon || "",
        followUpsCoverImage: form.followUpsCoverImage || "",
        followUpsTextColor: form.followUpsTextColor || "white",
        followUpsTextEffect: form.followUpsTextEffect || "shadow",
        followUpsOverlay: typeof form.followUpsOverlay === "number" ? form.followUpsOverlay : 55,
        defaultInactivityDays: Math.floor(n),
      });
      addAudit && addAudit(currentUser.id, currentUser.name, "Updated app settings", `App: "${trimmedName}" · Label: "${trimmedLabel}" · Inactivity default: ${Math.floor(n)}d`);
      addNotification && addNotification(`App settings updated`, "general");
      setSavedAt(Date.now());
    } catch (e) {
      setError(e.message || "Could not save settings.");
    } finally {
      setSaving(false);
      await reload();
    }
  };

  const previewLogo = form.appLogo && form.appLogo.length > 0 ? form.appLogo : resolveLogo("");

  return (
    <div data-testid="settings-page">
      <div style={{ marginBottom: 12 }}>
        <StructuralBackButton to="/dashboard" label="← Dashboard" testId="settings-back" />
      </div>

      {/* Branding */}
      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="paint-palette" size={18} /> Branding</div>
        <div className="form-group full mb-14">
          <label>App Logo</label>
          <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <img
              src={previewLogo}
              alt="Logo preview"
              data-testid="settings-logo-preview"
              style={{ width: 92, height: 92, borderRadius: 14, objectFit: "cover", border: "2px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", background: "var(--kc-surface)" }}
            />
            <PhotoUploader
              value={form.appLogo || null}
              name={form.appName || "Logo"}
              onChange={(v) => set("appLogo", v || "")}
              size={64}
              data-testid="settings-logo"
            />
          </div>
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
            PNG or JPG up to 2 MB. Remove to revert to the default Kingdom Connect crest.
          </div>
        </div>
        <div className="form-grid mb-14">
          <div className="form-group">
            <label>Church Name *</label>
            <input data-testid="settings-app-name" value={form.appName} onChange={e => set("appName", e.target.value)} maxLength={80} placeholder="Kingdom Connect" />
          </div>
          <div className="form-group">
            <label>Tagline</label>
            <input data-testid="settings-app-tagline" value={form.appTagline} onChange={e => set("appTagline", e.target.value)} maxLength={160} placeholder="To Communicate the Love of Christ" />
          </div>
        </div>
      </div>

      {/* Labels */}
      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="tag" size={18} /> Labels</div>
        <div className="form-group mb-14">
          <label>Follow-Ups Feature Label *</label>
          <input data-testid="settings-followups-label" value={form.followUpsLabel} onChange={e => set("followUpsLabel", e.target.value)} maxLength={40} placeholder="Follow-Ups" />
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
            Renames the feature throughout navigation, dashboard widgets, page titles, and the Departments card. Functionality and data are unchanged.
          </div>
        </div>
        <div className="form-group">
          <label>Follow-Ups Icon</label>
          <PhotoUploader
            value={form.followUpsIcon || ""}
            name={form.followUpsLabel || "Follow-Ups"}
            onChange={(val) => set("followUpsIcon", val || "")}
            size={72}
            data-testid="settings-followups-icon"
          />
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
            Optional custom icon shown on the Follow-Ups card inside the Departments page. PNG or JPG; auto-cropped + compressed below 200 KB. Remove to revert to the default chat icon.
          </div>
        </div>
        {/* Iter 83 — Follow-Ups cover image + text styling (parity with standard depts). */}
        <div className="form-group mb-14" style={{ marginTop: 18 }}>
          <label>Follow-Ups Cover Image</label>
          <CoverImage
            value={form.followUpsCoverImage || ""}
            onChange={(v) => set("followUpsCoverImage", v || "")}
            editable={true}
            title={form.followUpsLabel || "Follow-Ups"}
            iconName="chat"
            overlay={typeof form.followUpsOverlay === "number" ? form.followUpsOverlay : 55}
            textColor={form.followUpsTextColor || "white"}
            textEffect={form.followUpsTextEffect || "shadow"}
            data-testid="settings-followups-cover"
          />
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
            JPG, PNG, or WEBP up to 5 MB. Displays at 16:9 above the Follow-Ups department card.
          </div>
          <TextStyleControls
            textColor={form.followUpsTextColor || "white"}
            textEffect={form.followUpsTextEffect || "shadow"}
            overlay={typeof form.followUpsOverlay === "number" ? form.followUpsOverlay : 55}
            onColor={(v) => set("followUpsTextColor", v)}
            onEffect={(v) => set("followUpsTextEffect", v)}
            onOverlay={(v) => set("followUpsOverlay", v)}
            previewImage={form.followUpsCoverImage || ""}
            previewTitle={form.followUpsLabel || "Follow-Ups"}
            previewSubtitle="Visitor & member care tracking"
          />
        </div>
      </div>

      {/* Iter-74 — Dashboard Hero Images (day-of-week cycling) */}
      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <DashboardHeroImagesSection settings={settings} update={update} />
      </div>

      {/* Iter-86e — Hero Card Text Styling (per day of week) */}
      {isSuperAdmin && <HeroTextStylesSection settings={settings} update={update} />}

      {/* Defaults */}
      <div className="card" style={{ padding: 22, marginBottom: 18 }}>
        <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="tools" size={18} /> Defaults</div>
        <div className="form-group mb-14">
          <label>Default Follow-Up Inactivity Threshold (days) *</label>
          <input
            data-testid="settings-default-inactivity"
            type="number"
            min={1}
            max={365}
            value={form.defaultInactivityDays}
            onChange={e => set("defaultInactivityDays", e.target.value)}
            style={{ maxWidth: 160 }}
          />
          <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
            Used as the starting value for new team members. Each user&apos;s individually customised threshold (set on their record) remains editable and is unaffected by this default.
          </div>
        </div>
      </div>

      {/* Iter-86 — Background Wash (Super Admin only) */}
      {isSuperAdmin && <BackgroundWashSection settings={settings} reload={reload} />}

      {/* Calendar Categories */}
      <CalendarCategoriesSection settings={settings} reload={reload} />

      {/* Follow-Up Tree — threshold days */}
      {isSuperAdmin && <FollowUpTreeSettingsSection settings={settings} reload={reload} />}

      {/* Privacy Controls */}
      <PrivacyControlsSection settings={settings} reload={reload} />

      {/* Pastoral Appointments — type names, durations, phone numbers */}
      <AppointmentsSettingsSection settings={settings} reload={reload} />

      {/* Finance module — currency symbol + expense approval thresholds */}
      <FinanceSettingsSection settings={settings} reload={reload} />

      {/* Tax Receipt module — church legal name, address, CRA #, signatory, statement */}
      {isSuperAdmin && <ReceiptSettingsSection settings={settings} reload={reload} />}

      {error && <div data-testid="settings-error" style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.45)", color: "#ff9b9b", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
      {savedAt > 0 && !error && <div data-testid="settings-saved" style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.40)", color: "#4ade80", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="check" size={14} /> Settings saved.</div>}

      <div className="flex gap-8">
        <button className="btn btn-gold" data-testid="settings-save" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save Settings"}
        </button>
      </div>

      <CleanSlateDangerZone currentUser={currentUser} addAudit={addAudit} />

      <WipeFollowUpTreeZone currentUser={currentUser} addAudit={addAudit} />
    </div>
  );
}


// ============================================================
// Iter-86e — HeroTextStylesSection
// Super-Admin-only control for the Dashboard hero card text styling
// per day of week. Text colour is intentionally DECOUPLED from the
// app light/dark theme because the hero background photo determines
// what colour is readable, not the user's theme preference.
// Stored in appSettings.heroTextStyles as
//   { sunday: {color, shadow, stroke}, monday: {...}, ... }
// ============================================================
const HERO_DAYS = [
  { key: "sunday",    label: "Sunday" },
  { key: "monday",    label: "Monday" },
  { key: "tuesday",   label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday",  label: "Thursday" },
  { key: "friday",    label: "Friday" },
  { key: "saturday",  label: "Saturday" },
];
const HERO_DEFAULT_STYLE = { color: "white", shadow: true, stroke: false };

function HeroTextStylesSection({ settings, update }) {
  const initial = React.useMemo(() => {
    const src = settings?.heroTextStyles || {};
    const out = {};
    for (const d of HERO_DAYS) out[d.key] = { ...HERO_DEFAULT_STYLE, ...(src[d.key] || {}) };
    return out;
  }, [settings]);
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);
  useEffect(() => { setDraft(initial); }, [initial]);

  const setDay = (dayKey, patch) => {
    setDraft(prev => ({ ...prev, [dayKey]: { ...prev[dayKey], ...patch } }));
    setSaved(false);
  };
  const resetAll = () => {
    const fresh = {};
    for (const d of HERO_DAYS) fresh[d.key] = { ...HERO_DEFAULT_STYLE };
    setDraft(fresh);
    setSaved(false);
  };
  const save = async () => {
    setErr(""); setSaving(true); setSaved(false);
    try {
      await update({ heroTextStyles: draft });
      setSaved(true);
    } catch (e) {
      setErr(e.message || "Could not save hero text styles");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="hero-text-styles-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="edit" size={18} /> Hero Card Text Styling
      </div>
      <p style={{ fontSize: 12, color: "var(--kc-sub)", margin: "0 0 16px", lineHeight: 1.5 }}>
        Configure the greeting text colour, shadow, and stroke for each day&apos;s hero photo. These styles override the app light/dark theme so text remains readable against the day&apos;s background photo. &quot;Auto&quot; switches between white and black based on the day&apos;s overlay opacity.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {HERO_DAYS.map(d => {
          const entry = draft[d.key] || HERO_DEFAULT_STYLE;
          return (
            <div
              key={d.key}
              data-testid={`hero-text-row-${d.key}`}
              style={{
                display: "grid",
                gridTemplateColumns: "minmax(88px, 100px) 1fr auto auto",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                background: "var(--kc-surface)",
                border: "1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent)",
                borderRadius: 10,
                flexWrap: "wrap",
              }}
            >
              <div style={{ fontFamily: "'Cinzel',serif", fontSize: 12, fontWeight: 700, color: "var(--kc-text)", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                {d.label}
              </div>
              <div style={{ display: "inline-flex", gap: 6 }}>
                {["white", "black", "auto"].map(c => {
                  const active = entry.color === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      data-testid={`hero-text-color-${d.key}-${c}`}
                      onClick={() => setDay(d.key, { color: c })}
                      aria-pressed={active}
                      style={{
                        padding: "6px 10px",
                        borderRadius: 999,
                        border: active ? "2px solid var(--kc-accent)" : "1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent)",
                        background: c === "white" ? "#ffffff" : c === "black" ? "#111111" : "linear-gradient(90deg,#ffffff 50%,#111111 50%)",
                        color: c === "black" ? "#ffffff" : "#111111",
                        fontFamily: "'Montserrat',sans-serif",
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                        cursor: "pointer",
                        minWidth: 60,
                      }}
                    >
                      {c}
                    </button>
                  );
                })}
              </div>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--kc-sub)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  data-testid={`hero-text-shadow-${d.key}`}
                  checked={!!entry.shadow}
                  onChange={e => setDay(d.key, { shadow: e.target.checked })}
                />
                Shadow
              </label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--kc-sub)", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  data-testid={`hero-text-stroke-${d.key}`}
                  checked={!!entry.stroke}
                  onChange={e => setDay(d.key, { stroke: e.target.checked })}
                />
                Stroke
              </label>
            </div>
          );
        })}
      </div>
      {err && <div data-testid="hero-text-error" style={{ marginTop: 10, background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.45)", color: "#ff9b9b", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>{err}</div>}
      {saved && <div data-testid="hero-text-saved" style={{ marginTop: 10, color: "var(--kc-accent)", fontSize: 12 }}>Saved. Reload the dashboard to see the change on the current day.</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14, flexWrap: "wrap" }}>
        <button className="btn btn-gold btn-sm" data-testid="hero-text-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Hero Text Styles"}
        </button>
        <button
          type="button"
          data-testid="hero-text-reset"
          onClick={resetAll}
          style={{ background: "transparent", border: "none", color: "var(--kc-accent)", cursor: "pointer", fontSize: 13, textDecoration: "underline", padding: 0 }}
        >
          Reset all to White + Shadow
        </button>
      </div>
    </div>
  );
}


// ============================================================
// Iter-86 — BackgroundWashSection
// Super-Admin-only control for the two-tone light-mode gradient
// wash (warm cream top, soft lavender bottom). Sliders update
// CSS custom properties live for real-time preview; Save PATCHes
// gradientTopOpacity / gradientBottomOpacity to app settings so
// all users see the new opacity on next reload.
// ============================================================
function BackgroundWashSection({ settings, reload }) {
  const initTop = typeof settings?.gradientTopOpacity === "number" ? settings.gradientTopOpacity : 0.6;
  const initBot = typeof settings?.gradientBottomOpacity === "number" ? settings.gradientBottomOpacity : 0.6;
  // Slider values are stored as integer percent (0..100) so the readouts
  // and thumb positions stay stable; we map to 0..1 floats when saving.
  const [topPct, setTopPct] = useState(Math.round(initTop * 100));
  const [botPct, setBotPct] = useState(Math.round(initBot * 100));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const t = typeof settings?.gradientTopOpacity === "number" ? settings.gradientTopOpacity : 0.6;
    const b = typeof settings?.gradientBottomOpacity === "number" ? settings.gradientBottomOpacity : 0.6;
    setTopPct(Math.round(t * 100));
    setBotPct(Math.round(b * 100));
  }, [settings]);

  // Live preview — mutate CSS vars on the document root as the SA drags.
  const applyLive = (t, b) => {
    document.documentElement.style.setProperty("--kc-gradient-top-opacity", String(t / 100));
    document.documentElement.style.setProperty("--kc-gradient-bottom-opacity", String(b / 100));
  };

  const onTop = (v) => { const n = Number(v); setTopPct(n); applyLive(n, botPct); };
  const onBot = (v) => { const n = Number(v); setBotPct(n); applyLive(topPct, n); };
  const resetToDefault = () => { setTopPct(60); setBotPct(60); applyLive(60, 60); };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          gradientTopOpacity: topPct / 100,
          gradientBottomOpacity: botPct / 100,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save background wash settings");
      }
      toast.success("Gradient settings saved. Changes apply on next reload for all users.");
      await reload();
    } catch (e) {
      setErr(e.message || "Could not save background wash settings");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="background-wash-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="paint-palette" size={18} /> Background Wash
      </div>
      <p style={{ fontSize: 12, color: "var(--kc-sub)", margin: "0 0 16px", lineHeight: 1.5 }}>
        Controls the visibility of the warm cream and soft lavender gradient wash on light mode pages. Changes apply on next page load for all users.
      </p>

      <div className="form-group mb-14">
        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Top Wash Strength (warm cream)</span>
          <span data-testid="wash-top-readout" style={{ fontVariantNumeric: "tabular-nums", color: "var(--kc-accent)", fontWeight: 700 }}>{topPct}%</span>
        </label>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={topPct}
          onChange={(e) => onTop(e.target.value)}
          data-testid="wash-top-slider"
          style={{ width: "100%" }}
        />
      </div>

      <div className="form-group mb-14">
        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Bottom Wash Strength (soft lavender)</span>
          <span data-testid="wash-bottom-readout" style={{ fontVariantNumeric: "tabular-nums", color: "var(--kc-accent)", fontWeight: 700 }}>{botPct}%</span>
        </label>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={botPct}
          onChange={(e) => onBot(e.target.value)}
          data-testid="wash-bottom-slider"
          style={{ width: "100%" }}
        />
      </div>

      {err && <div data-testid="background-wash-error" style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.45)", color: "#ff9b9b", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>{err}</div>}

      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <button className="btn btn-gold btn-sm" data-testid="background-wash-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Background Wash"}
        </button>
        <button
          type="button"
          data-testid="background-wash-reset"
          onClick={resetToDefault}
          style={{ background: "transparent", border: "none", color: "var(--kc-accent)", cursor: "pointer", fontSize: 13, textDecoration: "underline", padding: 0 }}
        >
          Reset to Default (60%)
        </button>
      </div>
    </div>
  );
}

function CalendarCategoriesSection({ settings, reload }) {
  const [colors, setColors] = useState((settings && settings.calendarCategoryColors) || {});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");
  useEffect(() => { setColors((settings && settings.calendarCategoryColors) || {}); }, [settings]);

  const setColor = (k, v) => setColors(c => ({ ...c, [k]: v }));
  const reset = (k) => setColors(c => { const n = { ...c }; delete n[k]; return n; });

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ calendarCategoryColors: colors }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save category colors");
      }
      setSavedAt(Date.now());
      await reload();
    } catch (e) {
      setErr(e.message || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="calendar-categories-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="paint-palette" size={18} /> Calendar Categories</div>
      <p style={{ fontSize: 12, color: "var(--kc-sub)", margin: "0 0 14px" }}>
        Assign a default color to each category. Items without an explicit color use the category default.
      </p>
      {CATEGORY_DEFS.map(cat => {
        const value = colors[cat.key] || "";
        const effective = value || cat.fallback;
        return (
          <div key={cat.key} data-testid={`cat-color-row-${cat.key}`} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
            <div style={{ width: 130, fontFamily: "'Montserrat',sans-serif", fontWeight: 700, color: "var(--kc-text)", fontSize: 13 }}>{cat.label}</div>
            <span aria-hidden="true" data-testid={`cat-color-swatch-${cat.key}`} style={{ display: "inline-block", width: 22, height: 22, borderRadius: "50%", background: effective, border: "1.5px solid rgba(255,255,255,0.18)" }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {COLOR_PRESETS.map(c => (
                <button
                  key={c.value}
                  type="button"
                  title={c.label}
                  data-testid={`cat-color-preset-${cat.key}-${c.value.replace("#", "")}`}
                  onClick={() => setColor(cat.key, c.value)}
                  style={{ width: 20, height: 20, borderRadius: "50%", background: c.value, border: value.toLowerCase() === c.value.toLowerCase() ? "2.5px solid #FFFFFF" : "1px solid rgba(255,255,255,0.18)", cursor: "pointer", padding: 0 }}
                />
              ))}
            </div>
            <input
              type="text"
              placeholder="#hex"
              maxLength={7}
              value={value}
              data-testid={`cat-color-hex-${cat.key}`}
              onChange={e => setColor(cat.key, e.target.value)}
              style={{ width: 90, padding: "4px 8px", fontSize: 12, borderRadius: 6, border: "1px solid rgba(255,255,255,0.20)", background: "var(--kc-surface)", color: "#fff" }}
            />
            <button type="button" className="btn btn-sm btn-outline" data-testid={`cat-color-reset-${cat.key}`} onClick={() => reset(cat.key)}>Reset</button>
          </div>
        );
      })}
      <div className="flex gap-8" style={{ marginTop: 10 }}>
        <button className="btn btn-gold btn-sm" data-testid="cat-colors-save" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Category Colors"}
        </button>
        {savedAt > 0 && !err && <span style={{ fontSize: 12, color: "#4ade80", display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="check" size={12} /> Saved</span>}
        {err && <span data-testid="cat-colors-err" style={{ fontSize: 12, color: "#ff9b9b" }}>{err}</span>}
      </div>
    </div>
  );
}

// ============================================================
// PrivacyControlsSection — Super Admin toggle for member-level
// profile-photo privacy. OFF by default → photos are always
// visible to every logged-in user. ON → the per-member "Profile
// Photo" privacy toggle appears in the privacy editor so each
// member can choose to hide their photo from non-privileged
// viewers (admins / their assigned pastor still see it).
// ============================================================
function PrivacyControlsSection({ settings, reload }) {
  const [enabled, setEnabled] = useState(!!settings?.allowPhotoPrivacy);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");
  useEffect(() => { setEnabled(!!settings?.allowPhotoPrivacy); }, [settings]);

  const save = async (next) => {
    setErr(""); setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ allowPhotoPrivacy: !!next }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save privacy controls");
      }
      setEnabled(!!next);
      setSavedAt(Date.now());
      await reload();
    } catch (e) {
      setErr(e.message || "Could not save");
      // revert UI on failure
      setEnabled(!!settings?.allowPhotoPrivacy);
    } finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="privacy-controls-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="lock" size={18} /> Privacy Controls</div>
      <label
        data-testid="settings-allow-photo-privacy-row"
        style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          padding: "10px 12px",
          background: enabled ? "color-mix(in srgb, var(--kc-accent) 5%, transparent)" : "rgba(255,255,255,0.03)",
          border: `1px solid ${enabled ? "color-mix(in srgb, var(--kc-accent) 30%, transparent)" : "rgba(255,255,255,0.12)"}`,
          borderRadius: 8, cursor: saving ? "wait" : "pointer",
        }}
      >
        <input
          type="checkbox"
          data-testid="settings-allow-photo-privacy"
          checked={enabled}
          disabled={saving}
          onChange={(e) => save(e.target.checked)}
          style={{ width: 18, height: 18, marginTop: 2, flexShrink: 0 }}
        />
        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <span style={{
            fontSize: 13, fontWeight: 700, color: "var(--kc-text)",
            fontFamily: "'Montserrat',sans-serif",
          }}>Allow members to hide their profile photo</span>
          <span style={{ fontSize: 11.5, color: "var(--kc-sub)", marginTop: 4, lineHeight: 1.5 }}>
            When ON, each member sees an extra <strong>Profile Photo</strong> toggle in their privacy section and may hide their photo from non-privileged viewers. Admins and a member&apos;s assigned pastor always see photos. When OFF (default), profile photos are visible to every logged-in user.
          </span>
        </span>
      </label>
      <div className="flex gap-8" style={{ marginTop: 10, alignItems: "center" }}>
        {savedAt > 0 && !err && <span style={{ fontSize: 12, color: "#4ade80", display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="check" size={12} /> Saved</span>}
        {err && <span data-testid="settings-allow-photo-privacy-err" style={{ fontSize: 12, color: "#ff9b9b" }}>{err}</span>}
      </div>
    </div>
  );
}




// ============================================================
// FinanceSettingsSection — Super Admin controls for the Finance
// module: currency symbol (used everywhere monetary amounts are
// rendered) + Expense Approvals thresholds (dollars in the UI,
// stored as cents on the backend). Auto-saves on Save button.
// ============================================================
function FinanceSettingsSection({ settings, reload }) {
  const [currency, setCurrency] = useState(settings?.currencySymbol || "$");
  const [leaderThreshold, setLeaderThreshold] = useState(
    Number.isFinite(Number(settings?.expenseDeptLeaderThresholdCents))
      ? (Number(settings.expenseDeptLeaderThresholdCents) / 100).toFixed(2)
      : "100.00"
  );
  const [adminThreshold, setAdminThreshold] = useState(
    Number.isFinite(Number(settings?.expenseAdminThresholdCents))
      ? (Number(settings.expenseAdminThresholdCents) / 100).toFixed(2)
      : "500.00"
  );
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    setCurrency(settings?.currencySymbol || "$");
    setLeaderThreshold(
      Number.isFinite(Number(settings?.expenseDeptLeaderThresholdCents))
        ? (Number(settings.expenseDeptLeaderThresholdCents) / 100).toFixed(2)
        : "100.00"
    );
    setAdminThreshold(
      Number.isFinite(Number(settings?.expenseAdminThresholdCents))
        ? (Number(settings.expenseAdminThresholdCents) / 100).toFixed(2)
        : "500.00"
    );
  }, [settings]);

  const save = async () => {
    setErr("");
    const sym = (currency || "").trim();
    const leaderCents = Math.round(Number(leaderThreshold) * 100);
    const adminCents = Math.round(Number(adminThreshold) * 100);
    if (!sym) { setErr("Currency symbol is required."); return; }
    if (sym.length > 6) { setErr("Currency symbol should be 1–6 characters."); return; }
    if (!Number.isFinite(leaderCents) || leaderCents < 0) { setErr("Department Leader threshold must be a non-negative number."); return; }
    if (!Number.isFinite(adminCents) || adminCents < 0) { setErr("Admin threshold must be a non-negative number."); return; }
    if (adminCents < leaderCents) { setErr("Admin threshold must be greater than or equal to Department Leader threshold."); return; }
    setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          currencySymbol: sym,
          expenseDeptLeaderThresholdCents: leaderCents,
          expenseAdminThresholdCents: adminCents,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save finance settings");
      }
      setSavedAt(Date.now());
      await reload();
    } catch (e) {
      setErr(e.message || "Could not save finance settings");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="finance-settings-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="coin" size={18} /> Finance
      </div>

      <div className="form-group mb-14">
        <label>Currency Symbol</label>
        <input
          type="text"
          data-testid="settings-currency-symbol"
          value={currency}
          onChange={(e) => setCurrency(e.target.value)}
          maxLength={6}
          placeholder="$"
          style={{ maxWidth: 140 }}
        />
        <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6, lineHeight: 1.5 }}>
          Rendered next to every amount in the Finance module. Examples: <code>$</code>, <code>CAD $</code>, <code>£</code>, <code>€</code>, <code>₦</code>, <code>KES</code>.
        </div>
      </div>

      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, opacity: 0.9, marginTop: 18 }}>
        <CustomIcon name="lock" size={14} /> Expense Approvals
      </div>
      <div style={{ fontSize: 12, color: "var(--kc-sub)", marginBottom: 12, lineHeight: 1.5 }}>
        Expenses below the Department Leader threshold require the Finance Leader&apos;s approval. Expenses at or above the Admin threshold require Super Admin or Limited Admin approval.
      </div>

      <div className="form-grid mb-14">
        <div className="form-group">
          <label>Department Leader Threshold ({currency || "$"})</label>
          <input
            type="number"
            step="0.01"
            min="0"
            data-testid="settings-expense-leader-threshold"
            value={leaderThreshold}
            onChange={(e) => setLeaderThreshold(e.target.value)}
            placeholder="100.00"
          />
        </div>
        <div className="form-group">
          <label>Admin Threshold ({currency || "$"})</label>
          <input
            type="number"
            step="0.01"
            min="0"
            data-testid="settings-expense-admin-threshold"
            value={adminThreshold}
            onChange={(e) => setAdminThreshold(e.target.value)}
            placeholder="500.00"
          />
        </div>
      </div>

      {err && <div data-testid="finance-settings-error" style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.45)", color: "#ff9b9b", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {savedAt > 0 && !err && <div data-testid="finance-settings-saved" style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.40)", color: "#4ade80", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="check" size={14} /> Finance settings saved.</div>}

      <button className="btn btn-gold btn-sm" data-testid="settings-finance-save" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save Finance Settings"}
      </button>
    </div>
  );
}


// ============================================================
// ReceiptSettingsSection — Super Admin controls for the Canadian
// tax-receipt module: church legal identity, CRA registration
// number, authorized signatory, and the standard charitable
// statement. Values live under `receiptSettings` inside
// kc_app_settings.
// ============================================================
function ReceiptSettingsSection({ settings, reload }) {
  const rs = settings?.receiptSettings || {};
  const [churchLegalName,  setChurchLegalName]  = useState(rs.churchLegalName  || "");
  const [churchAddress,    setChurchAddress]    = useState(rs.churchAddress    || "");
  const [charityRegNumber, setCharityRegNumber] = useState(rs.charityRegNumber || "");
  const [signatoryName,    setSignatoryName]    = useState(rs.signatoryName    || "");
  const [signatoryTitle,   setSignatoryTitle]   = useState(rs.signatoryTitle   || "");
  const [signatureImage,   setSignatureImage]   = useState(rs.signatureImage   || "");
  const [receiptFormat,    setReceiptFormat]    = useState(rs.receiptFormat    || "itemized");
  const [statementText,    setStatementText]    = useState(rs.statementText    || "No goods or services were received in exchange for this donation.");
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    const cur = settings?.receiptSettings || {};
    setChurchLegalName(cur.churchLegalName || "");
    setChurchAddress(cur.churchAddress || "");
    setCharityRegNumber(cur.charityRegNumber || "");
    setSignatoryName(cur.signatoryName || "");
    setSignatoryTitle(cur.signatoryTitle || "");
    setSignatureImage(cur.signatureImage || "");
    setReceiptFormat(cur.receiptFormat || "itemized");
    setStatementText(cur.statementText || "No goods or services were received in exchange for this donation.");
  }, [settings]);

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          receiptSettings: {
            churchLegalName:  churchLegalName.trim(),
            churchAddress:    churchAddress.trim(),
            charityRegNumber: charityRegNumber.trim(),
            signatoryName:    signatoryName.trim(),
            signatoryTitle:   signatoryTitle.trim(),
            signatureImage:   signatureImage || "",
            receiptFormat:    receiptFormat === "total_only" ? "total_only" : "itemized",
            statementText:    statementText.trim(),
          },
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save receipt settings");
      }
      setSavedAt(Date.now());
      await reload();
    } catch (e) {
      setErr(e.message || "Could not save receipt settings");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="receipt-settings-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="document" size={18} /> Tax Receipt Settings
      </div>
      <div style={{ fontSize: 12, color: "var(--kc-sub)", marginBottom: 14, lineHeight: 1.5 }}>
        Printed on every official donation receipt. Required for Canadian charitable receipts.
      </div>

      <div className="form-group mb-14">
        <label>Church Legal Name</label>
        <input
          type="text"
          data-testid="receipt-settings-church-legal-name"
          value={churchLegalName}
          onChange={(e) => setChurchLegalName(e.target.value)}
          placeholder="e.g. Kingdom Global Church"
        />
      </div>

      <div className="form-group mb-14">
        <label>Church Address</label>
        <textarea
          rows={3}
          data-testid="receipt-settings-church-address"
          value={churchAddress}
          onChange={(e) => setChurchAddress(e.target.value)}
          placeholder="123 Faith Ave, Toronto, ON M5V 1A1, Canada"
          style={{ width: "100%", padding: 10, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontFamily: "inherit", resize: "vertical" }}
        />
      </div>

      <div className="form-group mb-14">
        <label>Canadian Charity Registration Number</label>
        <input
          type="text"
          data-testid="receipt-settings-charity-reg-number"
          value={charityRegNumber}
          onChange={(e) => setCharityRegNumber(e.target.value)}
          placeholder="12345 6789 RR0001"
        />
        <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
          Format hint: <code>12345 6789 RR0001</code>
        </div>
      </div>

      <div className="form-grid mb-14">
        <div className="form-group">
          <label>Authorized Signatory Name</label>
          <input
            type="text"
            data-testid="receipt-settings-signatory-name"
            value={signatoryName}
            onChange={(e) => setSignatoryName(e.target.value)}
            placeholder="e.g. Pastor John Smith"
          />
        </div>
        <div className="form-group">
          <label>Authorized Signatory Title</label>
          <input
            type="text"
            data-testid="receipt-settings-signatory-title"
            value={signatoryTitle}
            onChange={(e) => setSignatoryTitle(e.target.value)}
            placeholder="e.g. Senior Pastor / Treasurer"
          />
        </div>
      </div>

      <div className="form-group mb-14">
        <label>Signature Image (optional)</label>
        <PhotoUploader
          value={signatureImage}
          onChange={setSignatureImage}
          maxSizeMB={2}
          data-testid="receipt-settings-signature-uploader"
        />
        <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
          PNG or JPG up to 2 MB. Automatically compressed to under 200 KB.
        </div>
      </div>

      <div className="form-group mb-14">
        <label>Receipt Format</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
          {[
            { key: "itemized",   label: "Itemized", desc: "One line per donation" },
            { key: "total_only", label: "Total Only", desc: "Single grand-total line" },
          ].map(opt => {
            const active = receiptFormat === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setReceiptFormat(opt.key)}
                data-testid={`receipt-settings-format-${opt.key}`}
                style={{
                  flex: "1 1 160px", padding: "12px 14px",
                  borderRadius: 10, border: `1px solid ${active ? "var(--kc-accent)" : "var(--kc-sub)"}`,
                  background: active ? "color-mix(in srgb, var(--kc-accent) 20%, transparent)" : "transparent",
                  color: active ? "var(--kc-accent)" : "var(--kc-sub)",
                  textAlign: "left", cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13 }}>{opt.label}</div>
                <div style={{ fontSize: 11, opacity: 0.75, marginTop: 2 }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="form-group mb-14">
        <label>Standard Charitable Receipt Statement</label>
        <textarea
          rows={3}
          data-testid="receipt-settings-statement"
          value={statementText}
          onChange={(e) => setStatementText(e.target.value)}
          style={{ width: "100%", padding: 10, borderRadius: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff", fontFamily: "inherit", resize: "vertical" }}
        />
        <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
          Rendered in italics near the bottom of every receipt.
        </div>
      </div>

      {err && <div data-testid="receipt-settings-error" style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.45)", color: "#ff9b9b", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {savedAt > 0 && !err && <div data-testid="receipt-settings-saved" style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.40)", color: "#4ade80", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="check" size={14} /> Receipt settings saved.</div>}

      <button className="btn btn-gold btn-sm" data-testid="receipt-settings-save" onClick={save} disabled={saving}>
        {saving ? "Saving…" : "Save Receipt Settings"}
      </button>
    </div>
  );
}


// ============================================================
// CleanSlateDangerZone — destructive admin action. Wipes all
// fictitious people-data, deletes all existing users, creates
// ONE new Super Admin. Behind a confirmation phrase + a
// password input. Snapshots everything before writing.
// ============================================================
function CleanSlateDangerZone({ currentUser, addAudit }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [newUsername, setNewUsername] = useState("admin");
  const [newPassword, setNewPassword] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  // Simple, easy-to-type confirmation phrase (no special punctuation that
  // varies between keyboards). Comparison is whitespace- and case-flexible.
  const REQUIRED_PHRASE = "CLEAN SLATE YES I AM SURE";
  const SERVER_PHRASE = "CLEAN SLATE — YES I AM SURE"; // what the backend expects
  const normalised = (s) =>
    (s || "")
      .replace(/[\u2014\u2013-]/g, " ")   // em-dash, en-dash, hyphen → space
      .replace(/\s+/g, " ")              // collapse whitespace
      .trim()
      .toUpperCase();
  const phraseOk = normalised(confirmText) === REQUIRED_PHRASE;
  const canSubmit =
    phraseOk &&
    newUsername.trim().length >= 3 &&
    newPassword.length >= 8 &&
    !running;

  const run = async () => {
    setErr(""); setResult(null); setRunning(true);
    try {
      const { API_BASE, authHeaders } = await import("../../lib/apiClient");
      const res = await fetch(`${API_BASE}/admin/clean-slate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          // Always send the exact phrase the backend requires, regardless
          // of which dash variant the user typed.
          confirmation: SERVER_PHRASE,
          newAdminUsername: newUsername.trim(),
          newAdminPassword: newPassword,
        }),
      });
      const body = await res.json();
      if (!res.ok) {
        setErr(body.detail || "Clean slate failed");
      } else {
        setResult(body);
        addAudit && addAudit(currentUser.id, currentUser.name, "Clean slate executed", `wiped=${body.wipedKeys?.length} keys, removedUsers=${body.removedUsers}, newAdmin=${body.newAdminUsername}`);
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="clean-slate-zone"
      className="card"
      style={{
        padding: 22,
        marginTop: 32,
        border: "1px solid rgba(239,68,68,0.55)",
        background: "rgba(239,68,68,0.05)",
      }}
    >
      <div className="form-section-title mb-12" style={{ color: "#ff9b9b", display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="warning" size={18} /> Danger Zone — Clean Slate
      </div>
      <div style={{ fontSize: 13, color: "var(--kc-sub)", lineHeight: 1.6, marginBottom: 14 }}>
        This wipes <strong>all</strong> members, families, follow-ups, attendance, audit logs, notifications, tasks, appointments, blockouts, donations, community needs, dashboard layouts, and announcements. <strong>All existing user accounts will be deleted</strong> and replaced by a single new Super Admin (which you set below). App branding, departments, positions, services, events, and the food-form schema are kept.
        <br /><br />
        Every wiped collection is auto-snapshotted to <code>_preclean_&lt;timestamp&gt;</code> for emergency recovery.
      </div>

      {!open && (
        <button
          data-testid="clean-slate-open"
          className="btn"
          style={{ background: "rgba(239,68,68,0.15)", color: "#ff9b9b", border: "1px solid rgba(239,68,68,0.55)" }}
          onClick={() => setOpen(true)}
        >
          I understand — open clean-slate panel
        </button>
      )}

      {open && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="form-group">
            <label>New Super Admin username (lowercase, ≥ 3 chars)</label>
            <input
              data-testid="clean-slate-username"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="form-group">
            <label>New Super Admin password (≥ 8 chars)</label>
            <input
              data-testid="clean-slate-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              placeholder="ChangeMe2026"
            />
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4 }}>
              You will be logged out after this completes; log back in with these credentials.
            </div>
          </div>
          <div className="form-group">
            <label>
              Type to confirm: <code style={{ color: "#ff9b9b" }}>{REQUIRED_PHRASE}</code>
            </label>
            <input
              data-testid="clean-slate-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              placeholder={REQUIRED_PHRASE}
              style={{
                borderColor: confirmText
                  ? (phraseOk ? "rgba(74,222,128,0.6)" : "rgba(239,68,68,0.6)")
                  : undefined,
              }}
            />
            <div style={{ fontSize: 11, marginTop: 4, color: phraseOk ? "#4ade80" : "rgba(255,255,255,0.55)" }}>
              {confirmText
                ? (phraseOk ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="check" size={12} /> Confirmation accepted</span> : <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="close" size={12} /> Doesn&apos;t match — typing is case- and dash-insensitive</span>)
                : "Case-insensitive · spaces and dashes are flexible"}
            </div>
          </div>
          {!canSubmit && (confirmText || newPassword || newUsername !== "admin") && (
            <div data-testid="clean-slate-hint" style={{ fontSize: 12, color: "var(--kc-sub)", padding: "8px 12px", background: "rgba(255,255,255,0.04)", borderRadius: 6 }}>
              {!phraseOk && <div>• Confirmation phrase not yet matched</div>}
              {newUsername.trim().length < 3 && <div>• Username must be at least 3 characters</div>}
              {newPassword.length < 8 && <div>• Password must be at least 8 characters</div>}
            </div>
          )}
          {err && (
            <div data-testid="clean-slate-error" style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.45)", color: "#ff9b9b", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
              {err}
            </div>
          )}
          {result && (
            <div data-testid="clean-slate-success" style={{ background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.40)", color: "#4ade80", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><CustomIcon name="check" size={14} /> Clean slate complete. Removed {result.removedUsers} users, wiped {result.wipedKeys?.length} keys, created admin <code>{result.newAdminUsername}</code>. {result.nextStep}</span>
              <br /><br />
              <button
                className="btn btn-gold"
                style={{ marginTop: 6 }}
                onClick={() => {
                  try {
                    localStorage.removeItem("kc_token");
                    localStorage.removeItem("kc_jwt");
                  } catch { /* no-op */ }
                  window.location.href = "/";
                }}
              >
                Log out now
              </button>
            </div>
          )}
          <div className="flex gap-8" style={{ marginTop: 6 }}>
            <button
              data-testid="clean-slate-run"
              className="btn"
              style={{
                background: canSubmit ? "rgba(239,68,68,0.85)" : "rgba(239,68,68,0.20)",
                color: "#fff",
                border: "1px solid rgba(239,68,68,0.85)",
                opacity: canSubmit ? 1 : 0.6,
                cursor: canSubmit ? "pointer" : "not-allowed",
              }}
              disabled={!canSubmit}
              onClick={run}
            >
              {running ? "Running clean slate…" : "Run clean slate"}
            </button>
            <button
              className="btn"
              data-testid="clean-slate-cancel"
              onClick={() => {
                setOpen(false); setConfirmText(""); setNewPassword(""); setErr(""); setResult(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// WipeFollowUpTreeZone — Iter-58: single-click Super Admin action
// that resets the Follow-Up tree to God + AL-001/002/003, empties
// all activity, nulls every member's memberNumber, and zeroes the
// slot + member-number counters. Behind a typed confirmation phrase.
// ============================================================
function WipeFollowUpTreeZone({ currentUser, addAudit }) {
  const [open, setOpen] = React.useState(false);
  const [confirmText, setConfirmText] = React.useState("");
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [err, setErr] = React.useState("");

  const REQUIRED_PHRASE = "WIPE TREE YES";
  const phraseOk = confirmText.trim().toUpperCase() === REQUIRED_PHRASE;

  const run = async () => {
    setErr(""); setResult(null); setRunning(true);
    try {
      const { API_BASE, authHeaders } = await import("../../lib/apiClient");
      const res = await fetch(`${API_BASE}/admin/wipe-followup-tree`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
      });
      const body = await res.json();
      if (!res.ok) {
        setErr(body.detail || "Wipe failed");
      } else {
        setResult(body);
        addAudit && addAudit(currentUser.id, currentUser.name, "Follow-Up tree wiped", `membersCleared=${body.membersCleared}, memberNumberCounter=${body.memberNumberCounter}`);
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      data-testid="wipe-tree-zone"
      className="card"
      style={{
        padding: 22,
        marginTop: 20,
        border: "1px solid rgba(239,68,68,0.55)",
        background: "rgba(239,68,68,0.05)",
      }}
    >
      <div className="form-section-title mb-12" style={{ color: "#ff9b9b", display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="warning" size={18} /> Danger Zone — Wipe Follow-Up Tree
      </div>
      <div style={{ fontSize: 13, color: "var(--kc-sub)", lineHeight: 1.6, marginBottom: 14 }}>
        Resets the entire Follow-Up tree to a clean slate. Keeps the <strong>God node</strong> + three empty <strong>Alpha slots</strong> (AL-001, AL-002, AL-003). Deletes every other tree node, every occupancy record, every activity/follow-up log, and clears the <strong>memberNumber</strong> from every person. Slot counters reset (AL back to 3, all others to 0), and the global member-number counter resets to 0.
        <br /><br />
        <strong style={{ color: "#ff9b9b" }}>This action is irreversible.</strong> Members and their profile data are preserved — only their tree placement and number are cleared.
      </div>

      {!open && (
        <button
          data-testid="wipe-tree-open"
          className="btn"
          style={{ background: "rgba(239,68,68,0.15)", color: "#ff9b9b", border: "1px solid rgba(239,68,68,0.55)" }}
          onClick={() => setOpen(true)}
        >
          I understand — open wipe panel
        </button>
      )}

      {open && !result && (
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="form-group">
            <label style={{ color: "#ff9b9b" }}>
              Type <code style={{ padding: "1px 6px", background: "#111", borderRadius: 4 }}>WIPE TREE YES</code> to confirm
            </label>
            <input
              data-testid="wipe-tree-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
              placeholder="Type the phrase exactly"
              style={phraseOk ? { border: "1.5px solid #4ade80" } : undefined}
            />
          </div>
          {err && (
            <div data-testid="wipe-tree-error" style={{ color: "#ff6b6b", fontSize: 12 }}>
              {err}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              data-testid="wipe-tree-cancel"
              className="btn btn-outline"
              onClick={() => { setOpen(false); setConfirmText(""); setErr(""); }}
              disabled={running}
            >
              Cancel
            </button>
            <button
              data-testid="wipe-tree-run"
              className="btn"
              style={{
                background: phraseOk ? "#ef4444" : "rgba(239,68,68,0.20)",
                color: phraseOk ? "#fff" : "rgba(255,155,155,0.6)",
                border: "1px solid rgba(239,68,68,0.65)",
                cursor: phraseOk && !running ? "pointer" : "not-allowed",
              }}
              disabled={!phraseOk || running}
              onClick={run}
            >
              {running ? "Wiping…" : "Wipe the Follow-Up Tree"}
            </button>
          </div>
        </div>
      )}

      {result && (
        <div
          data-testid="wipe-tree-result"
          style={{
            marginTop: 14,
            padding: 14,
            background: "rgba(74,222,128,0.10)",
            border: "1px solid rgba(74,222,128,0.55)",
            borderRadius: 10,
            color: "#4ade80",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>✓ Tree wiped successfully.</div>
          <div style={{ fontSize: 12, color: "var(--kc-sub)" }}>
            {result.membersCleared} member{result.membersCleared === 1 ? "" : "s"} unassigned · Slot counters: AL={result.slotCounters?.AL}, others=0 · memberNumberCounter=0
          </div>
          <button
            className="btn btn-outline btn-sm"
            style={{ marginTop: 10 }}
            onClick={() => { setOpen(false); setResult(null); setConfirmText(""); }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}


function AppointmentsSettingsSection({ settings, reload }) {
  const [form, setForm] = useState({
    apptShortName: settings?.apptShortName || "On-the-Fly Connections",
    apptShortDuration: settings?.apptShortDuration ?? 20,
    apptLongName: settings?.apptLongName || "Weekday Connections",
    apptLongDuration: settings?.apptLongDuration ?? 60,
    emergencyPhone: settings?.emergencyPhone || "",
    churchPhone: settings?.churchPhone || "",
  });
  useEffect(() => {
    setForm({
      apptShortName: settings?.apptShortName || "On-the-Fly Connections",
      apptShortDuration: settings?.apptShortDuration ?? 20,
      apptLongName: settings?.apptLongName || "Weekday Connections",
      apptLongDuration: settings?.apptLongDuration ?? 60,
      emergencyPhone: settings?.emergencyPhone || "",
      churchPhone: settings?.churchPhone || "",
    });
  }, [settings]);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      const r = await kcFetch(`${API_BASE}/admin/app-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          apptShortName: String(form.apptShortName || "").trim(),
          apptShortDuration: parseInt(form.apptShortDuration, 10) || 20,
          apptLongName: String(form.apptLongName || "").trim(),
          apptLongDuration: parseInt(form.apptLongDuration, 10) || 60,
          emergencyPhone: String(form.emergencyPhone || "").trim(),
          churchPhone: String(form.churchPhone || "").trim(),
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save appointment settings");
      }
      setSavedAt(Date.now());
      await reload();
    } catch (e) { setErr(e.message || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="card" data-testid="appointments-settings-card" style={{ padding: 22, marginBottom: 18 }}>
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}><CustomIcon name="calendar-clock" size={18} /> Pastoral Appointments</div>
      <p style={{ fontSize: 12, color: "var(--kc-sub)", margin: "0 0 14px" }}>
        Names and slot lengths shown on the public booking page, plus the phone numbers used by the crisis prompt and Away notice.
      </p>
      <div className="form-grid mb-12">
        <div className="form-group">
          <label>Short appointment type name</label>
          <input data-testid="settings-appt-short-name" value={form.apptShortName} onChange={e => set("apptShortName", e.target.value)} placeholder="On-the-Fly Connections" />
        </div>
        <div className="form-group">
          <label>Short appointment duration (minutes)</label>
          <input data-testid="settings-appt-short-duration" type="number" min={5} max={240} value={form.apptShortDuration} onChange={e => set("apptShortDuration", e.target.value)} />
        </div>
      </div>
      <div className="form-grid mb-12">
        <div className="form-group">
          <label>Long appointment type name</label>
          <input data-testid="settings-appt-long-name" value={form.apptLongName} onChange={e => set("apptLongName", e.target.value)} placeholder="Weekday Connections" />
        </div>
        <div className="form-group">
          <label>Long appointment duration (minutes)</label>
          <input data-testid="settings-appt-long-duration" type="number" min={5} max={240} value={form.apptLongDuration} onChange={e => set("apptLongDuration", e.target.value)} />
        </div>
      </div>
      <div className="form-grid mb-12">
        <div className="form-group">
          <label>On-call emergency phone number</label>
          <input data-testid="settings-emergency-phone" value={form.emergencyPhone} onChange={e => set("emergencyPhone", e.target.value)} placeholder="+233 ..." />
        </div>
        <div className="form-group">
          <label>Main church phone number</label>
          <input data-testid="settings-church-phone" value={form.churchPhone} onChange={e => set("churchPhone", e.target.value)} placeholder="+233 ..." />
        </div>
      </div>
      <div className="flex gap-8" style={{ marginTop: 6 }}>
        <button className="btn btn-gold btn-sm" data-testid="settings-appt-save" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save Appointment Settings"}
        </button>
        {savedAt > 0 && !err && <span style={{ fontSize: 12, color: "#4ade80", display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="check" size={12} /> Saved</span>}
        {err && <span data-testid="settings-appt-err" style={{ fontSize: 12, color: "#ff9b9b" }}>{err}</span>}
      </div>
    </div>
  );
}


function FollowUpTreeSettingsSection({ settings, reload }) {
  const [days, setDays] = React.useState(String(settings?.followUpThresholdDays ?? 30));
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [savedAt, setSavedAt] = React.useState(0);

  React.useEffect(() => {
    setDays(String(settings?.followUpThresholdDays ?? 30));
  }, [settings?.followUpThresholdDays]);

  const save = async () => {
    setErr(null); setSaving(true);
    try {
      const n = parseInt(days, 10);
      if (!Number.isFinite(n) || n < 1 || n > 365) throw new Error("Threshold must be between 1 and 365 days.");
      const token = localStorage.getItem("kc_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/settings`;
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ followUpThresholdDays: n }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Could not save follow-up threshold");
      }
      setSavedAt(Date.now());
      reload && reload();
    } catch (e) {
      setErr(e.message || "Could not save follow-up threshold");
    } finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ padding: 22, marginBottom: 18 }} data-testid="followup-tree-settings">
      <div className="form-section-title mb-12" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CustomIcon name="celebrate" size={18} /> Follow-Up Tree
      </div>
      <div className="form-group mb-14">
        <label>Follow-Up Inactivity Threshold (days) *</label>
        <input
          data-testid="settings-followup-threshold"
          type="number" min={1} max={365}
          value={days}
          onChange={e => setDays(e.target.value)}
          style={{ maxWidth: 160 }}
        />
        <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 6 }}>
          Members with no follow-up logged beyond this window show an amber health dot.
          Beyond double this window they show red and Super Admin receives an escalated push notification.
          Default: 30 days.
        </div>
      </div>
      <div className="flex gap-8" style={{ marginTop: 6 }}>
        <button className="btn btn-gold btn-sm" data-testid="settings-followup-save" disabled={saving} onClick={save}>
          {saving ? "Saving…" : "Save Follow-Up Threshold"}
        </button>
        {savedAt > 0 && !err && <span data-testid="settings-followup-saved" style={{ fontSize: 12, color: "#4ade80", display: "inline-flex", alignItems: "center", gap: 4 }}><CustomIcon name="check" size={12} /> Saved</span>}
        {err && <span data-testid="settings-followup-err" style={{ fontSize: 12, color: "#ff9b9b" }}>{err}</span>}
      </div>
    </div>
  );
}

